import { storeAuthTokens } from '@/api/authTokenStorage';
import { REFRESH_TOKEN_KEY } from '@/api/index';
import { z } from 'zod/v4';

let inFlightRefresh: Promise<{ wasRefreshed: boolean }> | null = null;

const RefreshResponse = z.object({
  token: z.string().optional(),
  refreshToken: z.string().optional(),
});

const performRefresh = async (): Promise<{ wasRefreshed: boolean }> => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return { wasRefreshed: false };
  }

  try {
    const response = await fetch(`${import.meta.env.VITE_API_HOST}/api/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      return { wasRefreshed: false };
    }

    const body: unknown = await response.json();

    const parsed = RefreshResponse.safeParse(body);

    // The endpoint returns empty tokens when the refresh token cannot be
    // renewed (e.g. an expired new-world token), which we treat as a failure.
    if (!parsed.success || !parsed.data.token) {
      return { wasRefreshed: false };
    }

    storeAuthTokens({
      token: parsed.data.token,
      refreshToken: parsed.data.refreshToken ?? parsed.data.token,
    });
    return { wasRefreshed: true };
  } catch {
    return { wasRefreshed: false };
  }
};

/**
 * Refresh the auth token via `POST /api/token`, writing the same localStorage
 * keys as the Redux auth saga's `storeAuthTokens`. Concurrent callers share a
 * single in-flight request, so a burst of 401s triggers only one refresh.
 *
 * Lets TanStack Query fetchers renew a token without the Redux saga. Always
 * check `wasRefreshed` in the return value.
 */
export const refreshAuthTokens = async (): Promise<{
  wasRefreshed: boolean;
}> => {
  inFlightRefresh ??= performRefresh().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
};
